import type { CategoryDatum } from '#shared/types';
import { BarChart, DonutChart, DonutLegend } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { formatPercent, formatRupees, formatterFor } from './format';
import { heightForRows, showsAsDonut } from './report.logic';
import { toBarSeries, toDonutPoints } from './to-series';

/**
 * The dimension breakdown, as a chart and as the numbers.
 *
 * Shared by the volume and patient-mix sections, which break the same kind of
 * rows down by the same dimensions and differ only in what they count.
 */

interface BreakdownChartCardProps {
  /** The dimension's display name. */
  label: string;
  breakdown: readonly CategoryDatum[];
  /** The donut's centre figure and what it counts. */
  centre: { value: string; label: string };
  /** The bar series' name when there are too many rows for a donut. */
  seriesLabel: string;
}

export function BreakdownChartCard({
  label,
  breakdown,
  centre,
  seriesLabel,
}: BreakdownChartCardProps) {
  return (
    <Card className="c-an__cell" title={label}>
      {showsAsDonut(breakdown) ? (
        <>
          <DonutChart
            title={label}
            points={toDonutPoints(breakdown)}
            centre={centre}
            format={formatterFor('count')}
            size={200}
          />
          <DonutLegend points={toDonutPoints(breakdown)} format={formatterFor('count')} />
        </>
      ) : (
        <BarChart
          title={label}
          orientation="horizontal"
          series={[toBarSeries('count', seriesLabel, breakdown)]}
          format={formatterFor('count')}
          height={heightForRows(breakdown.length, 30, 200)}
          categoryLabel={label}
        />
      )}
    </Card>
  );
}

interface BreakdownTableCardProps {
  label: string;
  breakdown: CategoryDatum[];
  /** Header for the count column. */
  countHeader: string;
  /** Header for the money column. */
  secondaryHeader: string;
}

export function BreakdownTableCard({
  label,
  breakdown,
  countHeader,
  secondaryHeader,
}: BreakdownTableCardProps) {
  return (
    <Card className="c-an__cell c-an__cell--wide" title={`${label} — the numbers`} flush>
      <DataTable
        caption={`${label} breakdown`}
        columns={[
          { key: 'label', header: label, render: (row) => row.label },
          {
            key: 'value',
            header: countHeader,
            align: 'end',
            render: (row) => <span className="tnum">{row.value}</span>,
          },
          {
            key: 'share',
            header: 'Share',
            align: 'end',
            render: (row) => <span className="tnum">{formatPercent(row.share)}</span>,
          },
          {
            key: 'secondary',
            header: secondaryHeader,
            align: 'end',
            // A dash, never a zero. The column is absent for a role that may
            // not see money, and "Rs 0" would say something false.
            render: (row) => (
              <span className="tnum">
                {row.secondary === null ? '—' : formatRupees(row.secondary)}
              </span>
            ),
          },
        ]}
        rows={breakdown}
        rowKey={(row) => row.key}
      />
    </Card>
  );
}
